// ============================================
// APPLICATION - Reportes Hooks
// ============================================

import { useMutation, useQuery } from '@tanstack/react-query';
import { reportesRepository } from '../../infrastructure/http/reportes.repository';

// retry: false en los 9 hooks (no en el QueryClient global, que seguiría reintentando
// ventas/caja/fiados/etc.): con el timeout de 30s del cliente y el botón "Buscar"
// explícito de la pantalla, un reintento automático a los ~31s solo duplicaba la carga
// contra un pool que ya estaba lento. El usuario reintenta ahora con el botón.
//
// `busquedaId` (último parámetro, opcional) es el comprobante de useIniciarBusquedaReportes: el
// backend lo exige solo al plan gratuito y lo ignora en premium. No va en la query key: el
// resultado depende del rango, no del comprobante, y así una búsqueda repetida reutiliza el caché.

/**
 * Registra el clic en Buscar de Reportes: valida el rango según el plan, descuenta del cupo
 * diario del plan gratuito y devuelve el comprobante (busquedaId) que usan las consultas
 * siguientes. Repetir la misma búsqueda en pocos minutos no descuenta cupo.
 */
export const useIniciarBusquedaReportes = () => {
  return useMutation({
    mutationFn: ({ desde, hasta }: { desde: string; hasta: string }) =>
      reportesRepository.iniciarBusqueda(desde, hasta),
  });
};

/**
 * Hook para obtener datos de recaudación
 */
export const useRecaudacionChart = (
  desde: string,
  hasta: string,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'recaudacion', desde, hasta],
    queryFn: ({ signal }) => reportesRepository.getRecaudacionChart(desde, hasta, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para obtener top productos
 */
export const useTopProductos = (
  desde: string,
  hasta: string,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'top-productos', desde, hasta],
    queryFn: ({ signal }) => reportesRepository.getTopProductos(desde, hasta, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener top productos total venta
 */
export const useTopProductosTotalVenta = (
  desde: string,
  hasta: string,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'top-productos-venta', desde, hasta],
    queryFn: ({ signal }) => reportesRepository.getTopProductosTotalVenta(desde, hasta, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener utilidad y costo
 */
export const useUtilidadCosto = (
  desde: string,
  hasta: string,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'utilidad-costo', desde, hasta],
    queryFn: ({ signal }) => reportesRepository.getUtilidadCosto(desde, hasta, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener reporte diario
 */
export const useReporteDiario = (
  desde: string,
  hasta: string,
  pagina: number,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'diario', desde, hasta, pagina],
    queryFn: ({ signal }) => reportesRepository.getReporteDiario(desde, hasta, pagina, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener alertas de inventario
 */
export const useAlertasInventario = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['reportes', 'alertas-inventario'],
    queryFn: ({ signal }) => reportesRepository.getAlertasInventario({ signal }),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener totales generales
 */
export const useTotalesGenerales = (
  desde: string,
  hasta: string,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'totales-generales', desde, hasta],
    queryFn: ({ signal }) => reportesRepository.getTotalesGenerales(desde, hasta, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener detalle de ventas por producto
 */
export const useDetalleVentasProducto = (
  desde: string,
  hasta: string,
  pagina: number,
  tamano: number = 10,
  enabled: boolean = true,
  busquedaId?: number
) => {
  return useQuery({
    queryKey: ['reportes', 'detalle-ventas-producto', desde, hasta, pagina, tamano],
    queryFn: ({ signal }) =>
      reportesRepository.getDetalleVentasProducto(desde, hasta, pagina, tamano, { signal, busquedaId }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener detalle de ventas por categoría de almacén
 */
export const useDetalleVentasCategoriaAlmacen = (
  desde: string,
  hasta: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['reportes', 'detalle-ventas-categoria-almacen', desde, hasta],
    queryFn: ({ signal }) => reportesRepository.getDetalleVentasCategoriaAlmacen(desde, hasta, { signal }),
    enabled: enabled && !!desde && !!hasta,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};
